import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBdsLanguage } from '@/core/hooks/useBdsLanguage';
import './ScheduleSection.scss';

interface ScheduleSectionProps {
  selectedDate?: string;
  onScheduleTypeChange?: (type: 'now' | 'scheduled') => void;
  onDateTimeChange?: (date: string | Date, time: string) => void;
}

export const ScheduleSection: React.FC<ScheduleSectionProps> = ({
  selectedDate = new Intl.DateTimeFormat('en-GB').format(new Date()),
  onScheduleTypeChange,
  onDateTimeChange
}) => {
  const { t } = useTranslation();
  const bdsLanguage = useBdsLanguage();
  const [scheduleType, setScheduleType] = useState<'now' | 'scheduled'>('scheduled');
  
  // Use refs to track latest values and avoid closure issues
  const dateRef = useRef<string | Date>('');
  const timeRef = useRef<string>('');
  const currentDate = new Date();
  const [day, month, year] = selectedDate.split('/').map(Number);
  const selectedDateValue = new Date(year, month - 1, day);
  const checkBdsLanguage = bdsLanguage === 'es_ES' ? 'es-ES' : 'pt-BR';
  const formattedStartDate = new Intl.DateTimeFormat(checkBdsLanguage).format(currentDate);

  useEffect(() => {
    dateRef.current = selectedDate;
  }, [selectedDate]);

  useEffect(() => {
    const componentId = `schedule-section-${Date.now()}`;
    
    const handleRadioChange = (event: any) => {
      const value = event.detail?.value;
      const newType = value === 'post-now' ? 'now' : 'scheduled';
      setScheduleType(newType);
      onScheduleTypeChange?.(newType);
    };

    const handleDateChange = (event: any) => {
      const dateValue = event.detail?.value;
      
      // BDS datepicker returns a Date object, convert to string for storage
      if (typeof dateValue === 'string' || dateValue instanceof Date) {
        dateRef.current = dateValue; 
        setTimeout(() => {
          onDateTimeChange?.(dateRef.current, timeRef.current);
        }, 0);
      }
    };

    const handleTimeChange = (event: any) => {      
      let timeValue = event.detail?.value || event.target?.value || '';

      timeValue = applyTimeMask(timeValue);
      
      timeRef.current = timeValue;

      const timePicker = document.getElementById('schedule-timepicker') as any;
      if (timePicker && timePicker.value !== timeValue) {
        timePicker.value = timeValue;
      }

      setTimeout(() => {
        onDateTimeChange?.(dateRef.current, timeRef.current);
      }, 0);
    };
    
    const applyTimeMask = (value: string): string => {
      const numbers = value.replaceAll(/\D/g, '');

      // Apply mask: HH:MM
      if (numbers.length === 0) return '';
      if (numbers.length <= 2) return numbers;
      if (numbers.length <= 4) return `${numbers.slice(0, 2)}:${numbers.slice(2)}`;
      return `${numbers.slice(0, 2)}:${numbers.slice(2, 4)}`;
    };

    const attachListeners = () => {
      const radioGroup = document.getElementById('schedule-radio-group');
      const datePicker = document.getElementById('schedule-datepicker');
      const timePicker = document.getElementById('schedule-timepicker');

      if (radioGroup && !radioGroup.dataset.listenersAttached) {
        radioGroup.addEventListener('bdsRadioGroupChange', handleRadioChange);
        radioGroup.dataset.listenersAttached = componentId;
      }

      if (datePicker && !datePicker.dataset.listenersAttached) {
        datePicker.addEventListener('bdsStartDate', handleDateChange);
        datePicker.addEventListener('bdsChange', handleDateChange);
        datePicker.dataset.listenersAttached = componentId;
      }

      if (timePicker && !timePicker.dataset.listenersAttached) {
        timePicker.addEventListener('bdsChange', handleTimeChange);
        timePicker.addEventListener('bdsInput', handleTimeChange);
        timePicker.dataset.listenersAttached = componentId;
      }
    };

    // Try immediate attachment
    attachListeners();
    
    // Also try after a small delay to catch late-rendered elements
    const timeoutId = setTimeout(attachListeners, 100);

    return () => {
      clearTimeout(timeoutId);
      // Clear component-specific markers to prevent listener duplication
      const elements = [
        document.getElementById('schedule-radio-group'),
        document.getElementById('schedule-datepicker'), 
        document.getElementById('schedule-timepicker')
      ];
      
      for (const element of elements) {
        if (element?.dataset.listenersAttached === componentId) {
          delete element.dataset.listenersAttached;
        }
      }
    };
  }, [onScheduleTypeChange, onDateTimeChange]);

  return (
    <bds-paper class="scheduler-section scheduler-schedule-section" border width="auto">
      <bds-grid direction="column" gap="2" lg="6" md="6" sm="6" xs="6" padding="none">
        <div className="schedule-section_header">
          <bds-typo class="schedule-section_header-title" tag="h4" variant="fs-20" bold="bold" margin={false}>
            {t('scheduleSection.title')}
          </bds-typo>
          <bds-typo tag="p" variant="fs-12">
            {t('scheduleSection.subtitle')}
          </bds-typo>
        </div>

        <bds-radio-group id="schedule-radio-group">
          <bds-grid direction="row" gap="4">
            <bds-radio value="scheduled" label={t('scheduleSection.scheduleOption')} checked></bds-radio>
            <bds-radio value="post-now" label={t('scheduleSection.postNowOption')}></bds-radio>
          </bds-grid>
        </bds-radio-group>

        <div 
          className="container-schedule-section"
          style={{ display: scheduleType === 'scheduled' ? 'block' : 'none' }}
        >
          <bds-typo variant="fs-16" bold="bold" margin={false}>
            {t('scheduleSection.schedulingTitle')}
          </bds-typo>
          
          <bds-grid direction="row" gap="4">
            <bds-datepicker 
              id="schedule-datepicker"
              type-of-date="single"
              label={t('scheduleSection.publicationDate')}
              start-date-limit={formattedStartDate}
              end-date-limit="31/12/2030"
              date-selected={selectedDateValue}
              value-date-selected={selectedDate}
              language={bdsLanguage}
            />
            
            <bds-input
              id="schedule-timepicker"
              label={t('scheduleSection.publicationTime')}
              placeholder={t('scheduleSection.timePlaceholder')}
              maxlength={5}
            />
          </bds-grid>
        </div>
      </bds-grid>
    </bds-paper>
  );
};
